// src/features/guides/OfflineSOSPage.jsx
// Standalone Offline SOS — for when the app itself can't reach the server.
// Accessible at /sos-offline from the OfflineBanner when fully offline.
//
// Does three things:
//   1. Gets GPS coordinates from device hardware (no internet needed)
//   2. Composes a standard emergency message with lat/lon
//   3. Offers three delivery paths:
//      a. SMS deep-link (opens native Messages, pre-filled)
//      b. Copy to clipboard
//      c. Web Share API (share to any app)
//
// This works even if the Eye on Storm server is completely unreachable.
// GPS works offline. SMS uses the cellular SS7 channel, not data.
// This is the last resort when everything else fails.

import { useState, useEffect } from 'react';
import { T, S }                from '@components/theme';

const SOS_NUMBER = import.meta.env.VITE_SMS_INBOUND_NUMBER ?? '';

const PRESET_MESSAGES = [
  {
    id: 'trapped',
    emoji: '🌊',
    label: 'Trapped / Flooding',
    template: (lat, lon, note) =>
      `🆘 SOS - TRAPPED by flooding. GPS: ${lat},${lon}. ${note ? note + '. ' : ''}Need rescue. Eye on Storm app.`,
  },
  {
    id: 'medical',
    emoji: '🏥',
    label: 'Medical Emergency',
    template: (lat, lon, note) =>
      `🆘 SOS - MEDICAL EMERGENCY. GPS: ${lat},${lon}. ${note ? note + '. ' : ''}Need immediate help. Eye on Storm app.`,
  },
  {
    id: 'general',
    emoji: '🆘',
    label: 'General Emergency',
    template: (lat, lon, note) =>
      `🆘 SOS - I need assistance. GPS: ${lat},${lon}. ${note ? note + '. ' : ''}Eye on Storm app.`,
  },
];

export default function OfflineSOSPage() {
  const [step,     setStep]    = useState('compose');  // compose | ready
  const [locState, setLoc]     = useState({ status:'idle', lat:null, lon:null, error:null });
  const [preset,   setPreset]  = useState('trapped');
  const [note,     setNote]    = useState('');
  const [copied,   setCopied]  = useState(false);
  const [shared,   setShared]  = useState(false);

  // Get GPS on mount — don't wait for user to tap
  useEffect(() => {
    setLoc({ status:'loading', lat:null, lon:null, error:null });
    if (!navigator.geolocation) {
      setLoc({ status:'error', lat:null, lon:null, error:'GPS not available on this device' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLoc({
        status: 'ok',
        lat:    pos.coords.latitude.toFixed(6),
        lon:    pos.coords.longitude.toFixed(6),
        acc:    Math.round(pos.coords.accuracy),
        error:  null,
      }),
      (err) => setLoc({
        status: 'error', lat:null, lon:null,
        error: err.code === 1 ? 'Location permission denied' : 'Could not get GPS location',
      }),
      { timeout:12000, enableHighAccuracy:true, maximumAge:30000 }
    );
  }, []);

  const selectedPreset = PRESET_MESSAGES.find(p => p.id === preset) ?? PRESET_MESSAGES[0];

  const lat = locState.lat ?? '??.??????';
  const lon = locState.lon ?? '-??.??????';
  const message = selectedPreset.template(lat, lon, note);

  const handleSMS = () => {
    const to  = SOS_NUMBER ? SOS_NUMBER.replace(/\D/g, '') : '';
    const url = to
      ? `sms:+${to}?body=${encodeURIComponent(message)}`
      : `sms:?body=${encodeURIComponent(message)}`;
    window.location.href = url;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback: select a textarea
      const el = document.getElementById('sos-message-text');
      if (el) { el.select(); document.execCommand('copy'); }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title: 'SOS — Emergency', text: message });
      setShared(true);
    } catch {}
  };

  const mapsUrl = locState.lat
    ? `https://maps.apple.com/?ll=${locState.lat},${locState.lon}`
    : null;

  return (
    <div style={S.page}>
      {/* Header — urgent red */}
      <div style={{ background:'#7f1d1d', padding:'14px 16px 12px', flexShrink:0 }}>
        <div style={{ fontWeight:900, fontSize:17, color:'#fff', marginBottom:4 }}>
          🆘 Offline SOS
        </div>
        <div style={{ fontSize:10, color:'rgba(255,255,255,0.7)' }}>
          Works without internet · Uses GPS + SMS
        </div>
      </div>

      {/* 911 sticky */}
      <a href="tel:911" style={{
        display:'flex', alignItems:'center', justifyContent:'center',
        gap:8, padding:'10px', background:'#dc2626',
        textDecoration:'none', flexShrink:0,
      }}>
        <span style={{ fontSize:18 }}>📞</span>
        <span style={{ fontSize:13, fontWeight:900, color:'#fff' }}>
          Call 911 first for life-threatening emergencies
        </span>
      </a>

      <div style={S.scroll}>
        <div style={{ padding:'14px' }}>

          {/* GPS status */}
          <div style={{
            background: locState.status==='ok'
              ? 'rgba(5,150,105,0.08)'
              : locState.status==='error'
                ? 'rgba(220,38,38,0.08)'
                : 'rgba(217,119,6,0.08)',
            border:`1px solid ${locState.status==='ok' ? 'rgba(5,150,105,0.25)' : locState.status==='error' ? 'rgba(220,38,38,0.25)' : 'rgba(217,119,6,0.25)'}`,
            borderRadius:10, padding:'10px 12px', marginBottom:14,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:locState.status==='ok'?4:0 }}>
              <span style={{ fontSize:16 }}>
                {locState.status==='ok' ? '📍' : locState.status==='loading' ? '⏳' : '⚠️'}
              </span>
              <div style={{ fontWeight:800, fontSize:12,
                color: locState.status==='ok' ? '#059669' : locState.status==='error' ? '#dc2626' : T.amber }}>
                {locState.status==='ok'    ? `GPS locked (±${locState.acc}m)` :
                 locState.status==='loading' ? 'Getting GPS location…' :
                 locState.error}
              </div>
            </div>
            {locState.status==='ok' && (
              <div style={{ fontSize:10, color:T.dim, fontFamily:'monospace' }}>
                {locState.lat}, {locState.lon}
                {mapsUrl && (
                  <a href={mapsUrl} target="_blank" rel="noreferrer"
                    style={{ marginLeft:8, color:T.blue, fontSize:9 }}>
                    View on map →
                  </a>
                )}
              </div>
            )}
            {locState.status==='error' && (
              <div style={{ fontSize:10, color:T.dim, marginTop:3 }}>
                Your GPS coordinates will appear as ??. The message will still be useful
                if you can describe your location manually below.
              </div>
            )}
          </div>

          {/* Message type */}
          <div style={{ fontSize:10, fontWeight:700, color:T.dim,
                        textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>
            Situation
          </div>
          <div style={{ display:'flex', gap:6, marginBottom:12 }}>
            {PRESET_MESSAGES.map(p => (
              <button key={p.id} onClick={() => setPreset(p.id)} style={{
                flex:1, padding:'8px 4px', borderRadius:10, border:'1.5px solid',
                borderColor: preset===p.id ? '#dc2626' : T.bdr,
                background:  preset===p.id ? 'rgba(220,38,38,0.08)' : 'transparent',
                cursor:'pointer', fontFamily:T.font,
                display:'flex', flexDirection:'column', alignItems:'center', gap:3,
              }}>
                <span style={{ fontSize:18 }}>{p.emoji}</span>
                <span style={{ fontSize:9, fontWeight:700,
                  color: preset===p.id ? '#dc2626' : T.dim,
                  textAlign:'center', lineHeight:1.2 }}>
                  {p.label}
                </span>
              </button>
            ))}
          </div>

          {/* Additional detail */}
          <div style={{ fontSize:10, fontWeight:700, color:T.dim,
                        textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>
            Add detail (optional)
          </div>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Describe your exact location, how many people, injuries, what you need..."
            rows={3}
            style={{ width:'100%', padding:'10px 12px', borderRadius:9,
                     border:`1.5px solid ${T.bdr}`, background:T.card,
                     color:T.text, fontSize:12, fontFamily:T.font,
                     boxSizing:'border-box', outline:'none', resize:'vertical',
                     marginBottom:14 }}
          />

          {/* Composed message preview */}
          <div style={{ fontSize:10, fontWeight:700, color:T.dim,
                        textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>
            Your SOS message
          </div>
          <textarea
            id="sos-message-text"
            readOnly
            value={message}
            rows={4}
            style={{ width:'100%', padding:'10px 12px', borderRadius:9,
                     border:`1.5px solid rgba(220,38,38,0.3)`,
                     background:'rgba(220,38,38,0.04)',
                     color:T.text, fontSize:11, fontFamily:'monospace',
                     boxSizing:'border-box', outline:'none', resize:'none',
                     marginBottom:16 }}
          />

          {/* Action buttons */}
          <div style={{ fontSize:10, fontWeight:700, color:T.dim,
                        textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
            Send this message
          </div>

          {/* SMS — primary, most reliable */}
          <button onClick={handleSMS} style={{
            width:'100%', padding:'14px', borderRadius:12, border:'none',
            background:'#0369a1', color:'#fff', fontSize:14, fontWeight:900,
            cursor:'pointer', fontFamily:T.font, marginBottom:8,
            display:'flex', alignItems:'center', justifyContent:'center', gap:10,
            boxShadow:'0 4px 12px rgba(3,105,161,.3)',
          }}>
            <span style={{ fontSize:20 }}>📱</span>
            <div style={{ textAlign:'left' }}>
              <div>Send via SMS</div>
              <div style={{ fontSize:10, opacity:0.8, fontWeight:500 }}>
                Opens Messages app · Works without data
              </div>
            </div>
          </button>

          {/* Copy */}
          <button onClick={handleCopy} style={{
            width:'100%', padding:'12px', borderRadius:12,
            border:`1.5px solid ${T.bdr}`, background:T.card,
            color: copied ? '#059669' : T.text, fontSize:13, fontWeight:800,
            cursor:'pointer', fontFamily:T.font, marginBottom:8,
          }}>
            {copied ? '✓ Copied to clipboard' : '📋 Copy to clipboard'}
          </button>

          {/* Share */}
          {navigator.share && (
            <button onClick={handleShare} style={{
              width:'100%', padding:'12px', borderRadius:12,
              border:`1.5px solid ${T.bdr}`, background:T.card,
              color: shared ? '#059669' : T.text, fontSize:13, fontWeight:800,
              cursor:'pointer', fontFamily:T.font, marginBottom:8,
            }}>
              {shared ? '✓ Shared' : '↗ Share (WhatsApp, Signal, etc.)'}
            </button>
          )}

          {/* Explanation */}
          <div style={{ padding:'10px 12px', background:'rgba(3,105,161,0.06)',
                        border:`1px solid rgba(3,105,161,0.15)`,
                        borderRadius:9, fontSize:10, color:T.dim, lineHeight:1.6 }}>
            <strong style={{ color:T.text }}>How this works offline:</strong> GPS uses your device's
            hardware — no internet. SMS travels over the cellular voice network (SS7), not data,
            so it works even when the internet is down. Copy/share lets you paste the message
            into any app once you have signal.
          </div>

          <div style={{ height:24 }} />
        </div>
      </div>
    </div>
  );
}
